/**
 * Cookie-based caching utility for storing and retrieving
 * small JSON-serializable data to avoid redundant API calls.
 */

const DEFAULT_MAX_AGE_SECONDS = 3600; // 1 hour

/**
 * Store a value in a cookie as a JSON string.
 * @param key - cookie name
 * @param value - JSON-serializable value
 * @param maxAgeSeconds - TTL in seconds (default 1 hour)
 */
export function setCookieCache<T>(
  key: string,
  value: T,
  maxAgeSeconds: number = DEFAULT_MAX_AGE_SECONDS
): void {
  try {
    const json = JSON.stringify(value);
    // Encode to handle special characters in JSON
    const encoded = encodeURIComponent(json);
    document.cookie = `${key}=${encoded}; path=/; max-age=${maxAgeSeconds}; SameSite=Lax`;
  } catch {
    // Silently fail — cookie storage is best-effort
  }
}

/**
 * Retrieve a cached value from a cookie.
 * @param key - cookie name
 * @returns parsed value or null if not found / invalid
 */
export function getCookieCache<T>(key: string): T | null {
  try {
    const match = document.cookie
      .split("; ")
      .find((row) => row.startsWith(`${key}=`));
    if (!match) return null;
    const encoded = match.split("=").slice(1).join("=");
    return JSON.parse(decodeURIComponent(encoded)) as T;
  } catch {
    return null;
  }
}

/**
 * Remove a cached cookie.
 * @param key - cookie name
 */
export function clearCookieCache(key: string): void {
  document.cookie = `${key}=; path=/; max-age=0`;
}
